/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React from 'react';

import { css, jsx } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import Anchor from '@atlaskit/primitives/anchor';
import Tag from '@atlaskit/tag/removable-tag';
import { token } from '@atlaskit/tokens';

import { Assignee as AssigneeComponent } from '../../../../../../../common/ui/jira-assignee';
import { getStatus } from '../../../../../../../common/ui/jira-status';
import { getJiraEntityIcon } from '../../../../../../../common/ui/product-entity-icon';
import { type CrossAppLinkHandlers } from '../../../../../../../common/utils/use-cross-app-link-handlers';
// TODO: extract this type out into its own file so it can be shared
import type { JiraIssueStatusAndColorName } from '../index';

export type SearchJiraIssueTableRowProps = {
	/** Issue type icon URL (or null to fall back to type-based icon). */
	iconUrl: string | null | undefined;
	/** The Jira result type (e.g. "issue", "epic"). */
	type: string;
	/** Resolved issue key, e.g. "PUP-002". */
	issueKey: string | undefined;
	/** Fully-qualified URL for the issue. */
	url: string;
	/**
	 * Cross-app interaction handlers for the issue link, from
	 * `useCrossAppLinkHandlers`. Spread onto the anchor so a wrapped URL reports its
	 * click; omitting them only means no analytic.
	 */
	crossAppLinkHandlers?: CrossAppLinkHandlers;
	/** Title / summary of the issue. */
	title: string | null | undefined;
	/** Status lozenge data — hydrated asynchronously, may be null while loading. */
	jiraIssueStatus?: JiraIssueStatusAndColorName | null;
	/** Assignee data. */
	assignee?: { name: string; picture: string } | null;
	/** Priority data from JiraPriorityField. */
	priority?: {
		readonly iconUrl: string | null | undefined;
		readonly name: string | null | undefined;
	} | null;
	/** Label names from JiraLabelsField.selectedLabelsConnection. */
	labels?: string[];
	/** Called when the user clicks anywhere on the row. */
	onClick?: () => void;
	/** Called when the user right-clicks (opens the context menu) anywhere on the row. */
	onContextMenu?: () => void;
};

const styles = {
	tr: css({
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		cursor: 'pointer',

		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& td:first-of-type': {
			// there is a top-level style that targets the first and last cell of any table rows
			// that removes padding so we need to override this by using a descendant selector
			paddingLeft: token('space.100'),

			// the left border is provided by the table
			borderLeftWidth: '0px',
		},

		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& td:last-of-type': {
			// there is a top-level style that targets the first and last cell of any table rows
			// that removes padding so we need to override this by using a descendant selector
			paddingRight: token('space.100'),

			// the right border is provided by the table
			borderRightWidth: '0px',
		},
	}),
	tdBase: css({
		display: 'table-cell',
		boxSizing: 'border-box',
		verticalAlign: 'middle',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		overflowX: 'hidden',
		whiteSpace: 'nowrap',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		// every table cell (except the first and last) will have a right and bottom border
		borderRightWidth: token('border.width'),
		borderBottomWidth: token('border.width'),
		borderTopWidth: '0px',
		borderLeftWidth: '0px',
	}),
	tdWork: css({
		// fixed-width breakpoints taken from Figma: https://www.figma.com/design/D6160TfMfREv8jBeAsw5ma/Blend-Search---Chat?node-id=2419-121123&t=56eOb7RsXea00pZJ-4
		'@media (min-width: 0px)': {
			width: '210px',
		},
		'@media (min-width: 1026px)': {
			width: '400px',
		},
		'@media (min-width: 1440px)': {
			width: '456px',
		},
	}),
	// Reporter (116px) is not implemented; its width is divided equally among the 4 non-Work columns.
	// 600px: 116/4 = 29px extra per column
	// 768px: 116/4 = 29px extra per column
	// 1026px: 146/4 ≈ 37px extra per column (rounded)
	// 1440px+: 146/4 ≈ 37px extra per column (rounded)
	tdStatus: css({
		'@media (min-width: 0px)': {
			width: '141px', // 112 + 29
		},
		'@media (min-width: 1026px)': {
			width: '149px', // 112 + 37
		},
	}),
	tdAssignee: css({
		'@media (min-width: 0px)': {
			width: '145px', // 116 + 29
		},
		'@media (min-width: 1026px)': {
			width: '183px', // 146 + 37
		},
	}),
	// TODO: add column and data to table — tdReporter kept for future use
	tdReporter: css({
		'@media (min-width: 0px)': {
			width: '116px',
		},
		'@media (min-width: 1026px)': {
			width: '146px',
		},
	}),
	tdLabels: css({
		'@media (min-width: 0px)': {
			// Labels not shown at 600px in Figma; width absorbed from Reporter redistribution
			width: '129px', // 100 + 29
		},
		'@media (min-width: 1026px)': {
			width: '137px', // 100 + 37
		},
	}),
	tdPriority: css({
		'@media (min-width: 0px)': {
			// Priority not shown at 600px in Figma; width absorbed from Reporter redistribution
			width: '89px', // 60 + 29
		},
		'@media (min-width: 1026px)': {
			width: '111px', // 74 + 37
		},
		'@media (min-width: 1440px)': {
			width: '77px', // 40 + 37
		},
	}),
	tdWorkInner: css({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.075'),
		overflow: 'hidden',
	}),
	tdPriorityInner: css({
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	}),
	link: css({
		flexShrink: 0,
		color: token('color.link'),
		fontWeight: token('font.weight.regular'),
	}),
	iconWrapper: css({
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	}),
	summary: css({
		flexGrow: 1,
		flexShrink: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	}),
};

// logic copied from /packages/search/search-common/src/utils/highlight-matched-text.tsx
const HIGHLIGHT_START_TAG = '@@@hl@@@';
const HIGHLIGHT_END_TAG = '@@@endhl@@@';
const cleanStartRegex = new RegExp(HIGHLIGHT_START_TAG, 'g');
const cleanEndRegex = new RegExp(HIGHLIGHT_END_TAG, 'g');
// used to remove the special highlighting characters from the Jira issue title that we get back from GQL
const removeSearchHighlighting = (source: string): string =>
	source.replace(cleanStartRegex, '').replace(cleanEndRegex, '');

export const SearchJiraIssueTableRow = ({
	iconUrl,
	type,
	issueKey,
	url,
	crossAppLinkHandlers,
	title,
	jiraIssueStatus,
	assignee,
	priority,
	labels = [],
	onClick,
	onContextMenu,
}: SearchJiraIssueTableRowProps): React.JSX.Element => {
	const icon = getJiraEntityIcon({ iconUrl, type, isIssue: true, size: 'small' });
	const statusInfo = { statusCategoryId: jiraIssueStatus?.statusCategoryId };
	const status =
		jiraIssueStatus != null ? getStatus(jiraIssueStatus.statusName, statusInfo, false) : null;
	const cleanTitle = removeSearchHighlighting(title ?? '');

	return (
		<tr
			css={styles.tr}
			data-testid="search-jira-issue-table-row"
			onClick={onClick}
			onContextMenu={onContextMenu}
		>
			{/* Work column: icon + issue key link + summary */}
			<td css={[styles.tdBase, styles.tdWork]}>
				<div css={styles.tdWorkInner}>
					<span css={styles.iconWrapper}>{icon}</span>
					{issueKey && (
						// No onClick here: the click handler lives on the parent <tr> and fires once
						// via event bubbling. Attaching it here as well would double-fire the
						// `advancedSearchResultLink clicked` analytics event when the link is clicked.
						// The row-level onClick fires `advancedSearchResultLink clicked` via
						// event bubbling, so that event must not be attached here too or it
						// double-fires. The cross-app handlers below are a different event and
						// belong on the anchor: only this link navigates, while the row's
						// clickable area covers cells that do not.
						<Anchor href={url} target="_blank" {...crossAppLinkHandlers}>
							{issueKey}
						</Anchor>
					)}
					<span css={styles.summary}>{cleanTitle}</span>
				</div>
			</td>

			{/* Status column */}
			<td css={[styles.tdBase, styles.tdStatus]}>{status || null}</td>

			{/* Assignee column */}
			<td css={[styles.tdBase, styles.tdAssignee]}>
				<AssigneeComponent user={assignee ?? null} />
			</td>

			{/* Labels column — first label chip (if any) */}
			<td css={[styles.tdBase, styles.tdLabels]}>
				{labels[0] && <Tag text={labels[0]} isRemovable={false} />}
			</td>

			{/* Priority column — priority icon + name */}
			<td css={[styles.tdBase, styles.tdPriority]}>
				<div css={styles.tdPriorityInner}>
					{priority?.iconUrl && (
						<span css={styles.iconWrapper}>
							<img src={priority.iconUrl} alt={priority.name ?? ''} width={16} height={16} />
						</span>
					)}
				</div>
			</td>
		</tr>
	);
};
